import React, { useContext as useContextCore, useMemo } from 'react'
import filter from 'lodash/filter'
import type { FilterQuery, Project, Task } from '@ztechapps/common.contract'

// eslint-disable-next-line lodash/import-scope
import type { EntityWithSelection, UseListWithSelection } from '@ztechapps/common.hook'
import { useListWithSelection } from '@ztechapps/common.hook'

export type TasksPerProjectProviderProps = {
  taskFilter?: FilterQuery<Task>
  onTaskSelect: (args: { tasks: EntityWithSelection<Task>[] }) => void
  children: React.ReactNode
}

export type TasksPerProjectContext = {
  activeProject: Project
  setActiveProject: React.Dispatch<React.SetStateAction<Project>>
  listWithSelection: UseListWithSelection<Task>
} & Omit<TasksPerProjectProviderProps, 'children'>

const context = React.createContext<TasksPerProjectContext>({} as TasksPerProjectContext)
const CoreProvider = context.Provider

export const TasksPerProjectProvider = ({
  children,
  taskFilter,
  onTaskSelect,
  ...props
}: TasksPerProjectProviderProps) => {
  const [activeProject, setActiveProject] = React.useState<Project>(null)

  const filteredTasks = useMemo(() => {
    //there should always be a default filter
    const _taskFilter = taskFilter ?? (() => true)

    const activeProjectTasks = activeProject?.tasks

    return filter<Task>(activeProjectTasks, _taskFilter)
  }, [activeProject, taskFilter])

  const listWithSelection = useListWithSelection(filteredTasks)

  return (
    <CoreProvider value={{ ...props, activeProject, setActiveProject, listWithSelection, onTaskSelect }}>
      {children}
    </CoreProvider>
  )
}

export const useTasksPerProjectContext = () => useContextCore(context)
